import type { Bond, Withdraw } from './terms';
export interface Co {
  name: string;
  bizno: string;
  ceo: string;
  mgrTel: string;
  fpName: string;
  fpTel: string;
  fpCo: string;
  wm: string;
  amt: string;
  /** 투자금액이 자동 기본값(1곳이면 전액 · 2곳 이상이면 나눠서)인지 — 직접 고치면 false (2026-10-31) */
  amtAuto?: boolean;
  existing: boolean;
  companyId?: string | null;
  fpId?: string;
  wmId?: string;
  /** 회사별 수수료 조건 (2026-09-24) — 없으면 defaultFee() 로 간주 */
  fee?: FeeTerms;
  /** 벤처인증 후 투자확인서 발급 방식 (2026-09-24) — 등록 때 고르고 14단계에서 바꿀 수 있다 */
  certPlan?: CertPlan;
  /** 투자 처리 방법 메모 (2026-10-16 지도사 「투자를 어떻게 처리하기로 했는지 — 전환사채 바로 회수 등 · 최초 등록 때 · 수시로 보게」)
   *  새 등록 때 꼭 적음(모르면 「미정」) · 프로젝트 머리 · 목록에 늘 보임 · 우리만 봄(고객 · 팀장님 화면에 안 나감) */
  investPlan?: string;
  /** 이 회사 대표의 조합 참여 방식 — gp 조합장 · lp 조합원 (2026-10-18 · 업무관리 「벤처 GP · 새 조합 / LP · 조합 참여」) */
  ceoRole?: 'gp' | 'lp';
}

/* ── 회사별 수수료 조건 ───────────────────────────────────────────
 *  벤처 수수료(금액·시기·부가세) + 부대비용 3종(GP교육·법무사·관납료)은 각각
 *  「별도 청구 / 벤처 수수료에 포함」과 「업체 청구 / FP 대납」을 따로 고른다. */
export type FeeBillTo = 'company' | 'fp';            // 업체 청구 | FP 대납
export type FeeMode = 'separate' | 'included';         // 별도 청구 | 벤처 수수료에 포함
export type FeeTiming = 'post' | 'pre' | 'split';      // 후불 | 선불 | 계약금+잔금
export interface FeeItem { mode: FeeMode; amount: string; billTo: FeeBillTo; }
export interface FeeTerms {
  amount: string;                 // 벤처 수수료 (원, 콤마 허용)
  timing: FeeTiming;
  vat: 'separate' | 'included';   // 부가세 별도 | 포함
  billTo: FeeBillTo;
  gpEdu: FeeItem;                 // GP 교육 수수료
  lawyer: FeeItem;                // 법무사 보수
  govFee: FeeItem;                // 벤처 관납료(심사비 등)
  memo: string;
}
export const FEE_EXTRAS: [keyof Pick<FeeTerms, 'gpEdu' | 'lawyer' | 'govFee'>, string][] = [
  ['gpEdu', 'GP교육 수수료'], ['lawyer', '법무사 보수'], ['govFee', '벤처 관납료'],
];
/** 이 회사에 받는 부대비용 — LP(조합원)로 참여한 회사는 GP 교육을 받지 않으니 「GP교육 수수료」 없음 (2026-10-18 지도사 「LP에게는 이거 보여 줄 필요 없음」) */
export function feeExtrasFor(co?: { ceoRole?: 'gp' | 'lp' } | null): typeof FEE_EXTRAS {
  return co && co.ceoRole === 'lp' ? FEE_EXTRAS.filter(([k]) => k !== 'gpEdu') : FEE_EXTRAS;
}
export function defaultFee(): FeeTerms {
  return {
    amount: '3,000,000', timing: 'post', vat: 'separate', billTo: 'company',
    gpEdu: { mode: 'separate', amount: '300,000', billTo: 'company' },
    lawyer: { mode: 'separate', amount: '', billTo: 'company' },
    govFee: { mode: 'separate', amount: '', billTo: 'company' },
    memo: '',
  };
}

/* ── 투자확인서 발급 방식 ────────────────────────────────────────
 *  ① capital   자본금(증자분) — 회차별 증자일·귀속연도
 *  ② cb_fund   금번 발행 전환사채를 조합이 인수 — 조합원별 출자 기준
 *  ③ cb_direct 금번 전환사채 말소등기 및 재등기 → 대표자가 직접 재인수 — 개인 직접투자
 *  대표자 마음이 바뀌면 중간에 변경할 수 있고, 변경 이력과 이전 방식 진행분은 보존한다. */
export type CertMode = 'capital_inc' | 'capital_new' | 'cb_fund' | 'cb_direct';
export interface CapitalRound { date: string; amount: string; taxYear: string; }
/** 변경 이력 — from/to 는 방식 코드를 쉼표로 이은 목록 ('' = 없음). 한 회사에 방식 복수 선택 가능(2026-09-24) */
export interface CertPlanChange { at: string; from: string; to: string; memo: string; }
export interface CertPlan {
  mode: CertMode | '';        // (호환) 대표 방식 = modes[0] — 예전 데이터는 mode 만 있다
  modes?: CertMode[];         // 선택된 방식 전부 — 예: 조합 인수 전환사채 + 자본금 증자분 (회사당 복수 발급)
  capital: { rounds: CapitalRound[] };
  direct: { buyer: string; when: string };
  history: CertPlanChange[];
}
/** 표시·정렬 순서 = 업무 순서: 조합 인수 CB(13단계 VICS 보고 직후의 본류) → 대표자 재인수 → 자본금 증자분(추가 발급) → 설립 자본금 (QA 2026-09-24) */
export const CERT_MODES: [CertMode, string, string][] = [
  ['cb_fund', '전환사채 · 조합 인수', '조합 → 법인 입금 · 조합원 출자이행'],
  ['cb_direct', '전환사채 말소 → 대표자 재인수', '말소등기·재등기 → 대표자 직접 인수'],
  ['capital_inc', '자본금 · 증자분', '증자 회차별 입금내역 · 귀속연도'],
  ['capital_new', '자본금 · 법인 최초 설립', '발기인 납입 · 발기인총회 의사록'],
];
export const CERT_SHORT: Record<CertMode, string> = { capital_inc: '증자분', capital_new: '설립 자본금', cb_fund: '조합 인수 CB', cb_direct: '대표자 재인수 CB' };
/** 자본금 계열(증자·설립)인지 — 회차 입력을 쓰는 방식 */
export const isCapitalMode = (m: CertMode | '' | undefined): boolean => m === 'capital_inc' || m === 'capital_new';
export const certModeLabel = (m: CertMode | '' | undefined): string => {
  const hit = CERT_MODES.find((x) => x[0] === m);
  return hit ? hit[1] : '미선택';
};
/** 선택된 방식 목록 (예전 단일 mode 데이터 호환) — CERT_MODES 순서로 */
export function planModes(plan: CertPlan | null | undefined): CertMode[] {
  if (!plan) return [];
  const raw: CertMode[] = Array.isArray(plan.modes) ? plan.modes : (plan.mode ? [plan.mode] : []);
  return CERT_MODES.map((x) => x[0]).filter((m) => raw.indexOf(m) >= 0);
}
export const certModesLabel = (modes: CertMode[]): string => (modes.length ? modes.map((m) => CERT_SHORT[m]).join(' + ') : '미선택');
export function defaultCertPlan(): CertPlan {
  return { mode: '', capital: { rounds: [] }, direct: { buyer: '', when: '' }, history: [] };
}

/** 진행 주체(담당 사무소) — 로움경영지도사무소 / (주)올댓벤처스. 두 회사 건이 한 목록에 섞이므로 목록·헤더에서 구분한다 (지도사 2026-09-24)
 *  값이 없는 예전 프로젝트는 로움으로 본다 */
export type Office = 'roum' | 'atv';
export const OFFICES: { v: Office; short: string; name: string }[] = [
  { v: 'roum', short: '로움', name: '로움경영지도사무소' },
  { v: 'atv', short: '올댓벤처스', name: '(주)올댓벤처스' },
];
export const officeOf = (p: { office?: unknown; collect?: unknown }): Office => {
  if (p.office === 'atv' || p.office === 'roum') return p.office;
  // office 를 적기 전(2026-09-11~) 자료수집 카드의 「로고」로 올댓벤처스를 고른 프로젝트는 그쪽으로 본다
  const c = p.collect as { gp?: { brand?: unknown }; lps?: { brand?: unknown }[] } | undefined;
  if (c && ((c.gp && c.gp.brand === 'atv') || (Array.isArray(c.lps) && c.lps.some((l) => !!l && l.brand === 'atv')))) return 'atv';
  return 'roum';
};
/** 올댓벤처스 어드민 안에서 띄운 화면(allthatventures.com/rv/ · 2026-10-17 지도사 「여기서도 되고 로움에서도 되게」) —
 *  진행 주체가 올댓벤처스로 고정(새 등록 · 진행 주체 바꾸기 막음 · 서버도 고정 — backend/services/ventureOffice) · 로움 화면에서는 null */
export const LOCKED_OFFICE: Office | null = typeof window !== 'undefined' && /^\/rv(\/|$)/.test(window.location.pathname) ? 'atv' : null;
/** 고객 자료제출 링크의 주소 — 고객 화면(/portal/…)은 로움 주소에만 있어 올댓벤처스 어드민 안에서도 로움 주소로 (2026-10-17) */
export const portalOrigin = (): string => (LOCKED_OFFICE ? 'https://roum-partners.com' : window.location.origin);
export const officeInfo = (o: Office): { v: Office; short: string; name: string } => OFFICES.find((x) => x.v === o) || OFFICES[0];

export interface Project {
  id: string;
  org: string;
  /** 투자 처리 방법 — 조합 단위(2026-10-19) · 예전 회사별 값(cos[].investPlan)은 projectPlan 이 읽어 준다 */
  investPlan?: string;
  /** 이체 구분 — 조합 통장 입금 뒤 법인 통장으로 바로(now) · 2주 대기(wait2w) · 미정('') (2026-10-28 · venture/terms) */
  withdraw?: Withdraw;
  /** 사채 처리 — 전환사채 소득공제 신청(deduct) · 바로 회수(redeem) · 미정('') (2026-10-28 · venture/terms) */
  bond?: Bond;
  /** 별표 — 신경 써야 하는 조합(2026-10-19) · 목록에서 위로 · PATCH /projects/star */
  star?: boolean;
  office?: Office;                            // 진행 주체 — 로움(기본) / 올댓벤처스
  group?: string;                             // 목록 그룹 이름 (2026-10-08 · 없으면 미분류) — 서버 PATCH /projects/group 이 이 칸만 고친다
  ceoRole?: 'gp' | 'lp';                      // 대표 참여 방식 — 업무관리 「벤처인증 (투자, GP/LP)」 업무에서 (2026-10-18 · services/ventureFromTask)
  addr: string;
  total: string;
  per: string;
  dur: string;
  gp: string;
  gpTel: string;
  gpEmail: string;
  regDate: string;
  fp: string;
  fpCo: string;
  cos: Co[];
  step: number;
  done?: boolean;
  collect?: Collect;
  master?: Master;
  docver?: DocVerMap;
  mhash?: string;
  s3?: S3State;
  s4?: StoreState;
  s5?: StoreState;
  s6?: StoreState;
  s7?: StoreState;
  s8?: StoreState;
  s9?: StoreState;
  s10?: StoreState;
  s11?: StoreState;
  s13?: StoreState;
  s15?: StoreState;
  tmco?: Record<string, TmCoInfo>;           // 업무관리 업체 정보 스냅샷 (companyId → 정보) — 13단계 VICS 자동 채움 (2026-09-24)
  k13res?: Record<string, K13Result[]>;      // 14단계 자료수집 포털 제출 결과 (collectProjectId → 행) — 키트 연동
  s3gen?: number;
  costep?: Record<string, number>;
  codone?: Record<string, boolean>;
  [key: string]: unknown;
}

export interface RegState {
  org: string;
  /** 투자 처리 방법 — 조합 단위(2026-10-19 지도사 「투자 처리는 회사 단위가 아니라 조합 단위로」) */
  investPlan: string;
  /** 이체 구분 · 사채 처리(2026-10-28 · venture/terms) */
  withdraw: Withdraw;
  bond: Bond;
  office: Office;
  addr: string;
  total: string;
  per: string;
  dur: string;
  gp: string;
  gpTel: string;
  gpEmail: string;
  cos: Co[];
}

/** 업무관리(TM) 업체 정보 — GET /projects/:id/companies (2026-09-24) */
export interface TmCoInfo {
  id: string; name: string; biz_no: string; ceo: string; ceo_phone: string; ceo_email: string;
  company_reg_num?: string | null; address?: string | null; biz_type?: string | null; established_date?: string | null;
  ceo_birth?: string | null; manager?: string | null; manager_phone?: string | null; manager_email?: string | null;
}
/** 14단계 자료수집 포털 제출 행 (api CollectResultRow 축약) */
export interface K13Result { title: string; type: string; status: string; value: string; uploadId?: string | null; }

export type View = 'list' | 'register' | 'detail' | 'lp';   // lp = LP 후보 메모 (2026-10-08)
export type Tab = 'progress' | 'reg' | 'collect' | 'master' | 'docs';

export const FP: string[] = ['한화생명', '삼성생명', '교보생명', 'DB생명', '기타'];

export const STEPS: [string, string][] = [
  ['기초정보 수집', '사업자번호·법인·대표자·소개자 + GP 자료 확보'],
  ['GP 교육이수', '엔젤투자교육 수료증 확보'],
  ['조합 결성계획 신청', '규약·결성계획서 생성 → VICS 등록'],
  ['고유번호증 발급', '세무서 접수 → 2영업일 후 수령'],
  ['조합통장 개설서류 발송', '은행구비서류 출력 → 우체국 접수'],
  ['GP 정보 요청', '잔고·거래내역·신용정보 수령'],
  ['조합결성 등록', '출자증표·명부 도장합성 → VICS 제출'],
  ['전환사채 발행등기', '법인 서류 수령 → 법무사 전달'],
  ['벤처기업 확인신청', '벤처확인시스템 제출'],
  ['심사비용 결제', '심사 수수료 결제'],
  // D배치 (지도사 확정 2026-07-17): 구 11단계(확인서 출력·수수료 청구)를 4단계로 분해 — 전부 피투자 법인별 진행
  ['벤처투자확인서 송부', '법인별 확인서류 송부'],
  ['확인서 출력', '벤처기업확인서 출력·전달'],
  // 2026-09-24 (지도사 확정): 투자확인서 발급 전에 VICS 투자실적 보고를 별도 단계로 — 법인별 1313·1319·1320 → 조합 [1329] 확정/전송
  ['VICS 투자실적 보고', '법인별 투자기업·유형·거래 등록 → 조합 확정/전송'],
  ['투자확인서 발급', '개인투자조합 투자확인서(소득공제) 발급'],
  ['수수료 청구', '법인별 수수료 청구·입금 확인'],
];
/** 저장소 이름은 단계 번호와 다르다 (데이터 호환): s11send=11 송부 · s11=12 출력·15 청구 · s13=13 VICS+14 발급 키트 */

export const DOCGROUPS: [string, string][] = [
  ['자동 생성', '규약·결성계획서·등록신청서·동의서·확인서·안내장 등'],
  ['도장 합성', '출자증표·조합원명부·변경내역 (인감 PNG 합성)'],
  ['날인 후 재업로드', '동의서·인감등록부·의사록'],
  ['외부 수령', '등기부등본·승인통보서·고유번호증'],
];

export function blankCo(): Co {
  return { name: '', bizno: '', ceo: '', mgrTel: '', fpName: '', fpTel: '', fpCo: '한화생명', wm: '', amt: '', existing: false, companyId: null, fpId: '', wmId: '' };
}

/** 조합의 투자 처리 방법 — 조합 칸(investPlan) · 없으면 예전 회사별 값을 모아(같으면 하나 · 다르면 「 / 」로) */
export function projectPlan(p: { investPlan?: string; cos?: { investPlan?: string }[] } | null | undefined): string {
  if (!p) return '';
  const own = String(p.investPlan || '').trim();
  if (own) return own;
  const list: string[] = [];
  (p.cos || []).forEach((c) => { const t = String((c && c.investPlan) || '').replace(/\s+/g, ' ').trim(); if (t && list.indexOf(t) < 0) list.push(t); });
  const real = list.filter((t) => t !== '미정');
  return (real.length ? real : list).join(' / ');
}
export function emptyReg(): RegState {
  return { org: '', investPlan: '', withdraw: '', bond: '', office: LOCKED_OFFICE || 'roum', addr: '', total: '', per: '1,000,000', dur: '5', gp: '', gpTel: '', gpEmail: '', cos: [blankCo()] };
}

export interface TypeOpt { v: string; l: string; icon: string; }
export const TYPES: TypeOpt[] = [
  { v: 'file', l: '파일', icon: '📎' },
  { v: 'text', l: '단문', icon: '━' },
  { v: 'text_long', l: '장문', icon: '≡' },
  { v: 'radio', l: '객관식', icon: '◉' },
  { v: 'checkbox', l: '체크박스', icon: '☑' },
];
export const typeLabel = (t: string): string => { const o = TYPES.find((x) => x.v === t); return o ? o.l : t; };
export const needsOptions = (t: string): boolean => t === 'radio' || t === 'checkbox';

export interface GuideLink { name: string; url: string; }
export interface GuideFile { name: string; filePath?: string; s3Key?: string; }
export interface Item {
  id: string;
  title: string;
  type: string;
  required: boolean;
  sensitive: boolean;
  map: string;
  maps?: string[];
  options?: string[];
  desc: string;
  links: GuideLink[];
  files: GuideFile[];
  open: boolean;
}
export interface Recip { name: string; role: string; tel: string; email: string; }
export interface SendRecip { name: string; tel: string; email: string; type: 'A' | 'B'; status: '성공' | '실패'; reason?: string; }
export interface SendLog { at: string; channel: string; recips: SendRecip[]; }
export type ResultStatus = 'approved' | 'pending' | 'rejected' | 'none';
export interface ResultRow {
  title: string;
  type: string;
  required: boolean;
  sensitive: boolean;
  grp: string | null;
  status: ResultStatus;
  value: string;
  date: string;
  uploadId?: string;
}
export interface PersonCard {
  id?: string;
  name: string;
  bizno: string;
  url: string;
  tel?: string;
  email?: string;
  mgrTel?: string;
  mgrEmail?: string;
  recips: Recip[];
  items: Item[];
  sent: boolean;
  results: ResultRow[] | null;
  sendLog?: SendLog[];
  collectProjectId?: string;
  brand?: 'roum' | 'atv';   // 고객 제출 화면 우측 상단 로고 — 기본 roum(로움경영지도사무소) · atv(올댓벤처스) (2026-09-11)
}
export interface CoCard {
  id: string;
  name: string;
  bizno: string;
  rep: string;
  items: Item[];
  companyId?: string | null;
}
export type AnyCard = PersonCard | CoCard;
export interface Collect {
  gp: PersonCard;
  lps: PersonCard[];
  cos: CoCard[];
}

export interface MasterGp {
  name: string; rrn: string; addr: string; zip: string;
  edu: string; career: string; tel: string; email: string; amt: string;
}
export interface MasterLp { name: string; rrn: string; addr: string; amt: string; rel: string; tel: string; referrers?: string; } // referrers: 소개자 복수 — 쉼표 구분 (D3)
export interface MasterEdu { name: string; org: string; period: string; date: string; }
/** 사업자등록 신청서 위임장 대리인 — 없으면 지도사 본인(DEFAULT_AGENT · 서버 venturegen/util.js 와 같은 값) */
export interface MasterAgent { name: string; rrn: string; tel: string; rel: string; }
export const DEFAULT_AGENT: MasterAgent = { name: '임우승', rrn: '930111-1803217', tel: '010-2629-3967', rel: '경영지도사' };
export interface MasterS4 {
  agent?: MasterAgent; // 비면 DEFAULT_AGENT · 이름을 비우면 대리인 없이 대표자 직접 신청 (2026-10-23)
  owner: string; ownerRrn: string; ownerAddr: string; ownerTel?: string; leasePeriod: string; leaseDate: string; openDate: string; area: string; // ownerTel: 임대차 서명란 (D3)
  owners?: { name: string; rrn: string; tel: string; addr: string }[]; // 임대인 복수 — [0]은 위 단수 필드와 동기 (2026-07-17)
}
export interface Master {
  org: string; addr: string; zip: string; total: string; unit: string; dur: string;
  gp: MasterGp;
  lps: MasterLp[];
  edu: MasterEdu;
  applyDate: string;
  /** 결성총회일 — 7단계 등록 서류(의사록 · 명부 · 출자증표 · LP 동의서 …) 날짜 · 비면 applyDate (2026-10-22 · regdate.ts) */
  regApplyDate?: string;
  uniqueNo: string;
  s4: MasterS4;
}

export interface DocVer { label: string; doc: string; pdf: string; kind: 'auto' | 'seal' | 'edit' | 'pdfonly'; s3doc?: string | null; s3pdf?: string | null; at?: string; /** 서류에 찍힌 날짜(YYYY-MM-DD · 2026-10-22부터) */ date?: string; }
export type DocVerMap = Record<string, DocVer[]>;
export interface StoreState {
  up: Record<string, string>;
  upKey?: Record<string, string>;
  src?: Record<string, string>;
  txt?: Record<string, string>;
  chk?: Record<string, boolean>;
}
export interface S3State { vics: { id: string; pw: string }; up: Record<string, string>; upKey?: Record<string, string>; }
export type StoreKey = 's4' | 's5' | 's6' | 's7' | 's8' | 's9' | 's10' | 's11' | 's11send' | 's13' | 's15';
// s15: 15줄 「정기·결산 보고」(조합 단위, 결성등록 후 반복) 저장소 — report.ts (2026-09-24)
// s11: 구 통합단계 체크 저장소 — 신 12(issued)·14(billed/paid)가 그대로 읽어 기존 진행 데이터 무손실 (D배치)
